import type { QueryClient } from '@tanstack/react-query';

export type InvalidateSellerListingCachesOptions = {
    /**
     * When true, drop private myProducts* cache entries instead of only marking them stale.
     * Useful after create/edit when navigating away and useProtectedQuery has refetchOnMount:false.
     */
    removePrivate?: boolean;
};

/**
 * Bust private seller listing caches (myProducts + onboarding-banner variant) and, when
 * sellerId is known, public profile + public listings used by /users/:userId.
 *
 * Prefix invalidation/removal matches useProtectedQuery keys like ['myProducts', { userId }].
 */
export async function invalidateSellerListingCaches(
    queryClient: QueryClient,
    sellerId?: string | null,
    options: InvalidateSellerListingCachesOptions = {}
): Promise<void> {
    const { removePrivate = false } = options;

    if (removePrivate) {
        queryClient.removeQueries({ queryKey: ['myProducts'] });
    } else {
        await queryClient.invalidateQueries({ queryKey: ['myProducts'] });
    }

    if (sellerId) {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['publicListings', sellerId] }),
            queryClient.invalidateQueries({ queryKey: ['publicProfile', sellerId] }),
        ]);
    }
}
